import { destinations, type Destination } from '@/mocks/destinations';
import { themes, type ThemeItem } from '@/mocks/themes';
import { wikiArticles, type WikiArticle } from '@/mocks/wikiArticles';
import { wikiArticlesEn } from '@/mocks/wikiArticles.en';
import { wikiArticlesZhTw } from '@/mocks/wikiArticles.zhTW';
import { wikiPolarArticles } from '@/mocks/wikiPolarArticles';
import { wikiPolarArticlesEn } from '@/mocks/wikiPolarArticles.en';
import { wikiPolarArticlesZhTw } from '@/mocks/wikiPolarArticles.zhTW';
import { wikiBrandArticles } from '@/mocks/wikiBrandArticles';
import { wikiBrandArticlesEn } from '@/mocks/wikiBrandArticles.en';
import { wikiBrandArticlesZhTw } from '@/mocks/wikiBrandArticles.zhTW';
import { getSeoMeta } from './meta';
import { getLangPrefix, stripLangPrefix } from '@/utils/locale';
import zh from '@/i18n/local/zh/common';
import {
  safariPackageDetails,
  safariCommonFaq,
} from '@/mocks/safariPackages';
import { cases } from '@/pages/customer-stories/data/stories';
import { publicUrl } from './url';
const BRAND_NAME = '自由旷野旅行';
const BRAND_ALT = 'Free Wilderness Travel';
const BRAND_ALT_SHORT = 'Free Wilderness';
const LEGAL_NAME = '成都自由旷野国际旅行社有限公司';
const LICENSE_NUMBER = 'L-SC-100448';
const BUSINESS_LICENSE = '91510100MAC5MEQD3F';
const ORG_ID = 'https://freewds.com/#organization';
const WEBSITE_ID = 'https://freewds.com/#website';
const PERSON_ID = 'https://freewds.com/#founder';
const LOGO =
  'https://static.readdy.ai/image/a9ea126f64294a9be0ae609ee9486601/9f0ea906176b895d984d1906f4b5fd53.jpeg';
const PHONE = '+86-191-8061-9944';
const EMAIL = 'izzie@freewds.com';

// 注册地址（法人登记地址）与办公地址分开标注：
// - 注册地址已核实为「中国（四川）自由贸易试验区成都高新区天府大道中段530号1栋36楼3608C号（自编号）」；
// - 办公地址（线下到访）为「成都市高新区东方希望天祥广场A座3608B」，二者不能混为一谈。
const REGISTERED_ADDRESS = {
  '@type': 'PostalAddress',
  streetAddress: '中国（四川）自由贸易试验区成都高新区天府大道中段530号1栋36楼3608C号（自编号）',
  addressLocality: '成都',
  addressRegion: '四川',
  addressCountry: 'CN',
};

const OFFICE_ADDRESS = {
  '@type': 'PostalAddress',
  streetAddress: '成都市高新区东方希望天祥广场A座3608B',
  addressLocality: '成都',
  addressRegion: '四川',
  addressCountry: 'CN',
};

const STATIC_NAMES: Record<string, string> = {
  '/': '首页',
  '/about': '关于我们',
  '/contact': '联系我们',
  '/custom': '定制旅行',
  '/custom-service': '私人定制服务',
  '/enterprise': '企业高端定制',
  '/africa': '非洲旅行',
  '/polar': '南北极旅行',
  '/destinations': '目的地',
  '/themes': '主题旅行',
  '/explore': '旅行灵感',
  '/academy': '自由旷野学院',
  '/wiki': '旷野百科',
  '/wiki/africa': '非洲百科',
  '/wiki/polar': '极地百科',
  '/wiki/brand': '品牌百科',
  '/safari-packages/tanzania/2027-all-asilia-northern-circuit': '2027 All Asilia 坦桑尼亚北部野奢环线',
  '/safari-packages/tanzania/2027-lemala-safari': '2027 坦桑尼亚 Lemala 野奢 Safari',
  '/safari-packages/east-africa/2026-andbeyond-safari': '2026 东非 8天7晚 野奢游猎',
};

// 面包屑名称按语言本地化：英文/繁体百科列表页输出对应语言的分类名称。
const LOCALIZED_STATIC_NAMES: Record<'en' | 'zh-TW', Record<string, string>> = {
  en: {
    '/': 'Home',
    '/wiki': 'Wilderness Wiki',
    '/wiki/africa': 'Africa Encyclopedia',
    '/wiki/polar': 'Polar Encyclopedia',
    '/wiki/brand': 'Brand Stories',
  },
  'zh-TW': {
    '/': '首頁',
    '/wiki': '曠野百科',
    '/wiki/africa': '非洲百科',
    '/wiki/polar': '極地百科',
    '/wiki/brand': '品牌百科',
  },
};

function localizedStaticName(cleanPath: string, langPrefix: 'en' | 'zh-TW' | null): string {
  if (langPrefix) {
    const localized = LOCALIZED_STATIC_NAMES[langPrefix][cleanPath];
    if (localized) return localized;
  }
  return STATIC_NAMES[cleanPath] || BRAND_NAME;
}

function normalizePath(pathname: string): string {
  let path = pathname || '/';
  if (path.length > 1 && path.endsWith('/')) {
    path = path.slice(0, -1);
  }
  return path;
}

// 单一经营主体节点：Organization 与 TravelAgency 合并为同一实体，
// 通过 @id 指向同一个经营主体，避免制造两个互不相关的公司实体。
function organizationNode() {
  return {
    '@context': 'https://schema.org',
    '@type': ['Organization', 'TravelAgency'],
    '@id': ORG_ID,
    name: BRAND_NAME,
    alternateName: [BRAND_ALT, BRAND_ALT_SHORT],
    legalName: LEGAL_NAME,
    url: publicUrl('/'),
    foundingDate: '2022',
    logo: LOGO,
    description: zh['seo.home.description'],
    brand: {
      '@type': 'Brand',
      name: BRAND_NAME,
      alternateName: BRAND_ALT,
    },
    identifier: [
      {
        '@type': 'PropertyValue',
        propertyID: '旅行社业务经营许可证',
        value: LICENSE_NUMBER,
      },
      {
        '@type': 'PropertyValue',
        propertyID: '统一社会信用代码',
        value: BUSINESS_LICENSE,
      },
    ],
    address: REGISTERED_ADDRESS,
    location: OFFICE_ADDRESS,
    telephone: PHONE,
    email: EMAIL,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: PHONE,
      contactType: 'customer service',
      email: EMAIL,
      availableLanguage: ['Chinese', 'English'],
    },
    founder: { '@id': PERSON_ID },
    sameAs: [],
  };
}

// 统一创始人实体：仅在首页 / 关于我们等合适页面输出，不每页重复。
// 没有项目内已核实的公开账号 URL 时，sameAs 保持为空，不猜测网址。
function personNode() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': PERSON_ID,
    name: '李志强',
    alternateName: '路人李志强',
    jobTitle: '自由旷野旅行创始人',
    url: `${publicUrl('/about')}#founder`,
    worksFor: { '@id': ORG_ID },
    sameAs: [],
  };
}

function website() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: BRAND_NAME,
    alternateName: BRAND_ALT,
    url: publicUrl('/'),
    inLanguage: 'zh-CN',
    publisher: { '@id': ORG_ID },
  };
}

function faqPage() {
  const items: Array<[string, string[]]> = [
    ['faq.q7', ['faq.a7_1']],
    ['faq.q8', ['faq.a8_1']],
    ['faq.q9', ['faq.a9_1']],
    ['faq.q10', ['faq.a10_1']],
    ['faq.q11', ['faq.a11_1']],
    ['faq.q12', ['faq.a12_1']],
    ['faq.q13', ['faq.a13_1']],
    ['faq.q14', ['faq.a14_1']],
    ['faq.q1', ['faq.a1_1', 'faq.a1_2', 'faq.a1_3']],
    ['faq.q2', ['faq.a2_1', 'faq.a2_2']],
    ['faq.q3', ['faq.a3_1', 'faq.a3_2', 'faq.a3_3']],
    ['faq.q4', ['faq.a4_1', 'faq.a4_2', 'faq.a4_3']],
    ['faq.q5', ['faq.a5_1', 'faq.a5_2']],
    ['faq.q6', ['faq.a6_1', 'faq.a6_2', 'faq.a6_3']],
  ];

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(([question, answers]) => ({
      '@type': 'Question',
      name: zh[question],
      acceptedAnswer: {
        '@type': 'Answer',
        text: answers.map((a) => zh[a]).join(' '),
      },
    })),
  };
}

function customFaqPage() {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '什么是私人定制旅行？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '私人定制旅行是指根据您的出行时间、预算、偏好和需求，由专属旅行顾问一对一量身设计的旅行方案。没有固定团期，没有固定路线，出发时间、天数、节奏完全由您决定。',
        },
      },
      {
        '@type': 'Question',
        name: '定制旅行需要提前多久预订？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '南极、北极等热门目的地建议提前6-12个月预订；非洲Safari建议提前3-6个月预订；一般目的地建议提前1-3个月预订。旺季出行需更早规划。',
        },
      },
      {
        '@type': 'Question',
        name: '定制旅行的费用包含哪些？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '定制旅行费用包含：落地后的目的地交通、精选住宿、景点门票、专属导游等服务。国际机票不包含在内，旅行保险建议客户自行购买。所有费用透明公开，无隐藏费用。',
        },
      },
    ],
  };
}

function webPage(path: string, title: string, description: string) {
  const lang = getLangPrefix(path);
  const inLanguage = lang === 'en' ? 'en' : lang === 'zh-TW' ? 'zh-TW' : 'zh-CN';
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description,
    url: publicUrl(path),
    inLanguage,
    isPartOf: { '@type': 'WebSite', name: BRAND_NAME, url: publicUrl('/') },
  };
}

function breadcrumbList(items: { name: string; path: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: publicUrl(item.path),
    })),
  };
}

function touristDestination(dest: Destination) {
  return {
    '@context': 'https://schema.org',
    '@type': 'TouristDestination',
    name: dest.name,
    description: dest.description || dest.slogan,
    url: publicUrl(`/destinations/${dest.id}`),
    image: dest.image,
    touristType: '高端定制旅行者',
    provider: { '@id': ORG_ID },
  };
}

// 目的地 FAQ 结构化数据：与页面可见问答同源，逐字一致。
function destinationFaqPage(dest: Destination) {
  if (!dest.faq || dest.faq.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: dest.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

function themeStructuredData(theme: ThemeItem) {
  return {
    '@context': 'https://schema.org',
    '@type': 'TravelAction',
    name: theme.name,
    description: theme.description || theme.slogan,
    url: publicUrl(`/themes/${theme.id}`),
    image: theme.heroImage || theme.image,
    provider: { '@id': ORG_ID },
    result: {
      '@type': 'TouristAttraction',
      name: theme.name,
      description: theme.slogan,
      touristType: '高端定制旅行者',
    },
  };
}

function travelAction(name: string, description: string, places: string[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'TravelAction',
    name,
    description,
    fromLocation: { '@type': 'Place', name: '中国' },
    toLocation: places.map((p) => ({ '@type': 'Place', name: p })),
  };
}

function collectionPage(
  name: string,
  description: string,
  path: string,
  hasPart: { name: string; path: string }[] = [],
  inLanguage?: string,
) {
  const result: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name,
    description,
    url: publicUrl(path),
  };
  if (inLanguage) {
    result.inLanguage = inLanguage;
  }
  if (hasPart.length > 0) {
    result.hasPart = hasPart.map((p) => ({
      '@type': 'WebPage',
      name: p.name,
      url: publicUrl(p.path),
    }));
  }
  return result;
}

function serviceSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: '自由旷野私人定制旅行服务',
    provider: { '@id': ORG_ID },
    description: '一对一私人定制旅行服务，从需求沟通、方案设计到行程落地，全程专属顾问陪伴。',
    serviceType: '私人定制旅行',
    areaServed: '全球',
    availableChannel: {
      '@type': 'ServiceChannel',
      serviceType: '在线咨询与电话沟通',
      serviceUrl: publicUrl('/custom-service'),
      servicePhone: PHONE,
    },
  };
}

function contactSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'TravelAgency',
    '@id': ORG_ID,
    name: BRAND_NAME,
    alternateName: BRAND_ALT,
    legalName: LEGAL_NAME,
    url: publicUrl('/'),
    description: '中国高端自然旅行定制服务商，专注非洲Safari、南北极及自然类野奢定制旅行',
    brand: {
      '@type': 'Brand',
      name: BRAND_NAME,
      alternateName: BRAND_ALT,
    },
    telephone: PHONE,
    email: EMAIL,
    address: REGISTERED_ADDRESS,
    location: OFFICE_ADDRESS,
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00',
      },
    ],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: '旅行顾问',
        telephone: PHONE,
        areaServed: 'CN',
        availableLanguage: ['Chinese', 'English'],
      },
    ],
  };
}

function wikiSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: '旷野百科 - 自由旷野旅行',
    description: '专业目的地知识百科，涵盖非洲Safari攻略、极地探险指南、签证办理、营地选择等深度旅行知识。',
    url: publicUrl('/wiki'),
    publisher: { '@id': ORG_ID },
  };
}

function wikiArticleSchema(article: typeof wikiArticles[0], basePath: string, inLanguage = 'zh-CN') {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.metaDescription,
    image: article.image,
    ...(article.date ? { datePublished: article.date } : {}),
    author: {
      '@type': 'Person',
      name: article.author,
      jobTitle: article.authorRole,
    },
    publisher: { '@id': ORG_ID },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': publicUrl(`${basePath}/${article.slug}`),
    },
    keywords: article.metaKeywords,
    articleSection: article.subcategory,
    inLanguage,
  };
}

function wikiArticleFaqPage(article: typeof wikiArticles[0]) {
  if (!article.faq || article.faq.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

function safariFaqPage() {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: safariCommonFaq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

function touristTrip(pkg: (typeof safariPackageDetails)[number]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'TouristTrip',
    name: pkg.name,
    description: pkg.seoDescription,
    url: publicUrl(pkg.path),
    touristType: '野奢Safari参考行程',
    provider: { '@id': ORG_ID },
  };
}

// 真实旅途回声集合页：与页面可见案例同源，URL 使用尾斜杠形式。
function customerStoriesCollectionLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '真实旅途回声',
    description:
      '自由旷野匿名客户回访与旅途实拍，记录坦桑尼亚、博茨瓦纳、卢旺达等行程中的真实旅途体验。',
    url: publicUrl('/customer-stories'),
    isPartOf: { '@type': 'WebSite', name: BRAND_NAME, url: publicUrl('/') },
    about: {
      '@type': 'ItemList',
      itemListElement: cases.map((story, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Article',
          headline: `${story.monthLabel}｜${story.destination}｜${story.tags[0]}`,
          description: story.summary,
          datePublished: story.monthLabel.replace('年', '-').replace('月', '-01'),
          about: { '@type': 'Thing', name: story.destination },
        },
      })),
    },
  };
}

export function getJsonLd(pathname: string): object[] {
  const path = normalizePath(pathname);
  const langPrefix = getLangPrefix(path);
  const cleanPath = stripLangPrefix(path);
  const inLanguage = langPrefix === 'en' ? 'en' : langPrefix === 'zh-TW' ? 'zh-TW' : 'zh-CN';
  const meta = getSeoMeta(path);

  if (path === '/') {
    return [
      webPage(path, meta.title, meta.description),
      organizationNode(),
      personNode(),
      website(),
      faqPage(),
    ];
  }

  if (path.startsWith('/destinations/')) {
    const id = path.slice('/destinations/'.length);
    const dest = destinations.find((d) => d.id === id);
    if (dest) {
      const result: object[] = [
        webPage(path, meta.title, meta.description),
        breadcrumbList([
          { name: '首页', path: '/' },
          { name: '目的地', path: '/destinations' },
          { name: dest.name, path },
        ]),
        touristDestination(dest),
      ];
      const faq = destinationFaqPage(dest);
      if (faq) result.push(faq);
      return result;
    }
  }

  if (path.startsWith('/themes/')) {
    const id = path.slice('/themes/'.length);
    const theme = themes.find((t) => t.id === id);
    if (theme) {
      return [
        webPage(path, meta.title, meta.description),
        breadcrumbList([
          { name: '首页', path: '/' },
          { name: '主题旅行', path: '/themes' },
          { name: theme.name, path },
        ]),
        themeStructuredData(theme),
      ];
    }
  }

  const safariPkg = safariPackageDetails.find((p) => p.path === path);
  if (safariPkg) {
    return [
      webPage(path, meta.title, meta.description),
      breadcrumbList([
        { name: '首页', path: '/' },
        { name: '非洲旅行', path: '/africa' },
        { name: safariPkg.name, path },
      ]),
      touristTrip(safariPkg),
      safariFaqPage(),
    ];
  }

  const homeName = langPrefix === 'en' ? 'Home' : langPrefix === 'zh-TW' ? '首頁' : '首页';
  const name = localizedStaticName(cleanPath, langPrefix);
  const result: object[] = [
    webPage(path, meta.title, meta.description),
    breadcrumbList([{ name: homeName, path: '/' }, { name, path }]),
  ];

  if (path === '/about') {
    result.push(organizationNode());
    result.push(personNode());
  }

  if (path === '/contact') {
    result.push(contactSchema());
  }

  if (path === '/customer-stories') {
    result.push(customerStoriesCollectionLd());
  }

  if (path === '/custom') {
    result.push(customFaqPage());
  }

  if (path === '/custom-service') {
    result.push(serviceSchema());
  }

  if (path === '/enterprise') {
    result.push(organizationNode());
  }

  if (path === '/africa') {
    result.push(
      travelAction('非洲 Safari 定制旅行', '深入非洲核心目的地，体验真正的Safari野奢之旅。', [
        '博茨瓦纳',
        '坦桑尼亚',
        '肯尼亚',
        '卢旺达',
      ]),
    );
  }

  if (path === '/polar') {
    result.push(
      travelAction('南北极极地探险', '探索南极半岛、北极斯瓦尔巴等极地核心目的地。', [
        '南极半岛',
        '北极斯瓦尔巴',
        '格陵兰岛',
        '冰岛',
      ]),
    );
  }

  if (path === '/destinations') {
    result.push(
      collectionPage('全球高端旅行目的地', meta.description, path, [
        { name: '六大旅行主题', path: '/themes' },
        { name: '旷野百科', path: '/wiki' },
      ]),
    );
  }

  if (path === '/themes') {
    result.push(collectionPage('旅行主题', meta.description, path));
  }

  if (path === '/explore') {
    result.push(
      collectionPage('旅行灵感', meta.description, path, [
        { name: '六大旅行主题', path: '/themes' },
        { name: '全球目的地', path: '/destinations' },
      ]),
    );
  }

  if (path === '/wiki') {
    result.push(wikiSchema());
  }

  if (cleanPath === '/wiki/africa') {
    const africaArticles =
      langPrefix === 'en' ? wikiArticlesEn : langPrefix === 'zh-TW' ? wikiArticlesZhTw : wikiArticles;
    const articlePathPrefix =
      langPrefix === 'en' ? '/en/wiki/africa' : langPrefix === 'zh-TW' ? '/zh-TW/wiki/africa' : '/wiki/africa';
    const listName = langPrefix === 'en' ? 'Africa Encyclopedia' : '非洲百科';
    const listDesc =
      langPrefix === 'en'
        ? 'Free Wilderness Africa Encyclopedia: in-depth guides to Botswana, Tanzania and Kenya.'
        : '自由曠野旅行非洲百科，涵蓋博茨瓦納、坦桑尼亞、肯亞等核心目的地深度攻略。';
    result.push(
      collectionPage(
        listName,
        listDesc,
        path,
        [...africaArticles.map((a) => ({ name: a.title, path: `${articlePathPrefix}/${a.slug}` }))],
        inLanguage,
      ),
    );
  }

  if (cleanPath.startsWith('/wiki/africa/')) {
    const slug = cleanPath.slice('/wiki/africa/'.length);
    const article =
      langPrefix === 'en'
        ? wikiArticlesEn.find((a) => a.slug === slug)
        : langPrefix === 'zh-TW'
          ? wikiArticlesZhTw.find((a) => a.slug === slug)
          : wikiArticles.find((a) => a.slug === slug);
    if (article) {
      const basePath =
        langPrefix === 'en' ? '/en/wiki/africa' : langPrefix === 'zh-TW' ? '/zh-TW/wiki/africa' : '/wiki/africa';
      result.push(wikiArticleSchema(article, basePath, inLanguage));
      const faq = wikiArticleFaqPage(article);
      if (faq) result.push(faq);
    }
  }

  if (cleanPath === '/wiki/polar') {
    const polarArticles =
      langPrefix === 'en' ? wikiPolarArticlesEn : langPrefix === 'zh-TW' ? wikiPolarArticlesZhTw : wikiPolarArticles;
    const articlePathPrefix =
      langPrefix === 'en' ? '/en/wiki/polar' : langPrefix === 'zh-TW' ? '/zh-TW/wiki/polar' : '/wiki/polar';
    const listName = langPrefix === 'en' ? 'Polar Encyclopedia' : '極地百科';
    const listDesc =
      langPrefix === 'en'
        ? 'Free Wilderness Polar Encyclopedia, covering Antarctic travel seasons, cruise routes, the Drake Passage, and Arctic destinations.'
        : '自由曠野旅行極地百科，涵蓋南極旅行季節、郵輪航線、德雷克海峽、北極目的地等極地探險深度攻略。';
    result.push(
      collectionPage(
        listName,
        listDesc,
        path,
        [...polarArticles.map((a) => ({ name: a.title, path: `${articlePathPrefix}/${a.slug}` }))],
        inLanguage,
      ),
    );
  }

  if (cleanPath.startsWith('/wiki/polar/')) {
    const slug = cleanPath.slice('/wiki/polar/'.length);
    const article =
      langPrefix === 'en'
        ? wikiPolarArticlesEn.find((a) => a.slug === slug)
        : langPrefix === 'zh-TW'
          ? wikiPolarArticlesZhTw.find((a) => a.slug === slug)
          : wikiPolarArticles.find((a) => a.slug === slug);
    if (article) {
      const basePath =
        langPrefix === 'en' ? '/en/wiki/polar' : langPrefix === 'zh-TW' ? '/zh-TW/wiki/polar' : '/wiki/polar';
      result.push(wikiArticleSchema(article, basePath, inLanguage));
      const faq = wikiArticleFaqPage(article);
      if (faq) result.push(faq);
    }
  }

  if (cleanPath === '/wiki/brand') {
    const brandArticles =
      langPrefix === 'en' ? wikiBrandArticlesEn : langPrefix === 'zh-TW' ? wikiBrandArticlesZhTw : wikiBrandArticles;
    const articlePathPrefix =
      langPrefix === 'en' ? '/en/wiki/brand' : langPrefix === 'zh-TW' ? '/zh-TW/wiki/brand' : '/wiki/brand';
    const listName = langPrefix === 'en' ? 'Brand Stories' : '品牌百科';
    const listDesc =
      langPrefix === 'en'
        ? 'Free Wilderness Brand Stories, featuring founder field notes, brand story, and people profiles.'
        : '自由曠野旅行品牌百科，包含創始人實地筆記、品牌故事與人物記錄。';
    result.push(
      collectionPage(
        listName,
        listDesc,
        path,
        [...brandArticles.map((a) => ({ name: a.title, path: `${articlePathPrefix}/${a.slug}` }))],
        inLanguage,
      ),
    );
  }

  if (cleanPath.startsWith('/wiki/brand/')) {
    const slug = cleanPath.slice('/wiki/brand/'.length);
    const article =
      langPrefix === 'en'
        ? wikiBrandArticlesEn.find((a) => a.slug === slug)
        : langPrefix === 'zh-TW'
          ? wikiBrandArticlesZhTw.find((a) => a.slug === slug)
          : wikiBrandArticles.find((a) => a.slug === slug);
    if (article) {
      const basePath =
        langPrefix === 'en' ? '/en/wiki/brand' : langPrefix === 'zh-TW' ? '/zh-TW/wiki/brand' : '/wiki/brand';
      result.push(wikiArticleSchema(article as typeof wikiArticles[0], basePath, inLanguage));
    }
  }

  return result;
}